(() => {
  const pmAgeVerificationMemoryKeys = new Set();
  let pmAgeVerificationOpenInstance = null;

  class PmAgeVerification extends HTMLElement {
    constructor() {
      super();
      this.pmAbortController = null;
      this.pmDialogElement = null;
      this.pmConfirmButton = null;
      this.pmDeclineButton = null;
      this.pmPreviousFocusElement = null;
      this.pmInertStates = new Map();
      this.pmIsOpen = false;
    }

    connectedCallback() {
      this.pmAbortController?.abort();
      this.pmAbortController = new AbortController();
      this.pmDialogElement = this.querySelector('[data-pm-age-verification-dialog]');
      this.pmConfirmButton = this.querySelector('[data-pm-age-verification-confirm]');
      this.pmDeclineButton = this.querySelector('[data-pm-age-verification-decline]');

      if (!this.pmDialogElement || !this.pmConfirmButton || !this.pmDeclineButton) {
        return;
      }

      const pmSignal = this.pmAbortController.signal;
      this.pmConfirmButton.addEventListener('click', this.pmHandleConfirm.bind(this), { signal: pmSignal });
      this.pmDeclineButton.addEventListener('click', this.pmHandleDecline.bind(this), { signal: pmSignal });
      this.pmDialogElement.addEventListener('keydown', this.pmHandleKeydown.bind(this), { signal: pmSignal });
      window.addEventListener('pageshow', this.pmHandlePageShow.bind(this), { signal: pmSignal });

      if (this.pmIsDesignMode()) {
        this.pmGetSectionElement()?.addEventListener('shopify:section:select', this.pmHandleSectionSelect.bind(this), { signal: pmSignal });
        this.pmGetSectionElement()?.addEventListener('shopify:section:deselect', this.pmHandleSectionDeselect.bind(this), { signal: pmSignal });
        return;
      }

      this.pmOpenIfNeeded();
    }

    disconnectedCallback() {
      this.pmAbortController?.abort();
      this.pmClose({ pmRestoreFocus: false });
      this.pmAbortController = null;
      this.pmDialogElement = null;
      this.pmConfirmButton = null;
      this.pmDeclineButton = null;
      this.pmPreviousFocusElement = null;
    }

    pmGetSectionElement() {
      return this.closest('.shopify-section');
    }

    pmIsDesignMode() {
      return this.dataset.pmAgeVerificationDesignMode === 'true';
    }

    pmIsEnabled() {
      return this.dataset.pmAgeVerificationEnabled === 'true';
    }

    pmIsRequired() {
      return this.dataset.pmAgeVerificationRequired === 'true';
    }

    pmGetMinimumAge() {
      const pmMinimumAge = Number.parseInt(this.dataset.pmAgeVerificationAge || '', 10);
      return Number.isInteger(pmMinimumAge) && pmMinimumAge >= 16 && pmMinimumAge <= 99 ? pmMinimumAge : 18;
    }

    pmGetStorageMode() {
      const pmStorageMode = this.dataset.pmAgeVerificationStorageMode;
      return ['session', '1', '7', '30'].includes(pmStorageMode) ? pmStorageMode : 'session';
    }

    pmGetStorageKey() {
      return `pm_age_verified_${this.pmGetMinimumAge()}_v1`;
    }

    pmGetStorage() {
      try {
        return this.pmGetStorageMode() === 'session' ? window.sessionStorage : window.localStorage;
      } catch (pmStorageError) {
        return null;
      }
    }

    pmHasValidVerification() {
      const pmStorageKey = this.pmGetStorageKey();

      if (pmAgeVerificationMemoryKeys.has(pmStorageKey)) {
        return true;
      }

      const pmStorage = this.pmGetStorage();
      if (!pmStorage) {
        return false;
      }

      try {
        const pmStoredValue = pmStorage.getItem(pmStorageKey);
        if (!pmStoredValue) {
          return false;
        }

        const pmVerification = JSON.parse(pmStoredValue);
        if (!pmVerification || typeof pmVerification !== 'object' || pmVerification.verified !== true || pmVerification.mode !== this.pmGetStorageMode()) {
          pmStorage.removeItem(pmStorageKey);
          return false;
        }

        if (this.pmGetStorageMode() === 'session') {
          return true;
        }

        if (typeof pmVerification.expiresAt !== 'number' || !Number.isFinite(pmVerification.expiresAt) || pmVerification.expiresAt <= Date.now()) {
          pmStorage.removeItem(pmStorageKey);
          return false;
        }

        return true;
      } catch (pmStorageError) {
        try {
          pmStorage.removeItem(pmStorageKey);
        } catch (pmRemovalError) {
          // Storage access can be blocked by browser privacy settings.
        }
        return false;
      }
    }

    pmStoreVerification() {
      const pmStorageKey = this.pmGetStorageKey();
      const pmStorageMode = this.pmGetStorageMode();
      const pmVerification = { verified: true, mode: pmStorageMode };

      if (pmStorageMode !== 'session') {
        pmVerification.expiresAt = Date.now() + Number(pmStorageMode) * 24 * 60 * 60 * 1000;
      }

      const pmStorage = this.pmGetStorage();
      if (!pmStorage) {
        pmAgeVerificationMemoryKeys.add(pmStorageKey);
        return;
      }

      try {
        pmStorage.setItem(pmStorageKey, JSON.stringify(pmVerification));
      } catch (pmStorageError) {
        pmAgeVerificationMemoryKeys.add(pmStorageKey);
      }
    }

    pmOpenIfNeeded() {
      if (!this.isConnected || this.pmIsOpen || !this.pmIsEnabled() || !this.pmIsRequired() || pmAgeVerificationOpenInstance) {
        return;
      }

      if (!this.pmHasValidVerification()) {
        this.pmOpen();
      }
    }

    pmOpen() {
      if (!this.isConnected || this.pmIsOpen || pmAgeVerificationOpenInstance) {
        return;
      }

      this.pmPreviousFocusElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.hidden = false;
      this.setAttribute('data-pm-age-verification-open', 'true');
      this.pmIsOpen = true;
      pmAgeVerificationOpenInstance = this;
      document.documentElement.classList.add('pm-age-verification-open');
      this.pmSetBackgroundInert(true);

      requestAnimationFrame(() => {
        if (this.pmIsOpen && this.pmConfirmButton) {
          this.pmConfirmButton.focus({ preventScroll: true });
        }
      });
    }

    pmClose({ pmRestoreFocus = true } = {}) {
      if (!this.pmIsOpen) {
        return;
      }

      this.pmIsOpen = false;
      if (pmAgeVerificationOpenInstance === this) {
        pmAgeVerificationOpenInstance = null;
      }
      this.removeAttribute('data-pm-age-verification-open');
      this.hidden = true;
      this.pmSetBackgroundInert(false);
      document.documentElement.classList.remove('pm-age-verification-open');

      const pmFocusElement = this.pmPreviousFocusElement;
      this.pmPreviousFocusElement = null;
      if (pmRestoreFocus && pmFocusElement?.isConnected && typeof pmFocusElement.focus === 'function') {
        pmFocusElement.focus({ preventScroll: true });
      }
    }

    pmSetBackgroundInert(pmShouldInert) {
      if (pmShouldInert) {
        Array.from(document.body.children).forEach((pmBodyChild) => {
          if (pmBodyChild.contains(this)) {
            return;
          }

          this.pmInertStates.set(pmBodyChild, pmBodyChild.inert);
          pmBodyChild.inert = true;
        });
        return;
      }

      this.pmInertStates.forEach((pmWasInert, pmBodyChild) => {
        if (pmBodyChild.isConnected) {
          pmBodyChild.inert = pmWasInert;
        }
      });
      this.pmInertStates.clear();
    }

    pmGetFocusableElements() {
      if (!this.pmDialogElement) {
        return [];
      }

      return Array.from(this.pmDialogElement.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((pmElement) => !pmElement.hidden && pmElement.getClientRects().length > 0);
    }

    pmHandleKeydown(pmEvent) {
      if (pmEvent.key !== 'Tab') {
        return;
      }

      const pmFocusableElements = this.pmGetFocusableElements();
      if (pmFocusableElements.length === 0) {
        pmEvent.preventDefault();
        this.pmDialogElement?.focus({ preventScroll: true });
        return;
      }

      const pmFirstElement = pmFocusableElements[0];
      const pmLastElement = pmFocusableElements[pmFocusableElements.length - 1];
      if (pmEvent.shiftKey && document.activeElement === pmFirstElement) {
        pmEvent.preventDefault();
        pmLastElement.focus({ preventScroll: true });
      } else if (!pmEvent.shiftKey && document.activeElement === pmLastElement) {
        pmEvent.preventDefault();
        pmFirstElement.focus({ preventScroll: true });
      }
    }

    pmHandleConfirm() {
      if (this.pmIsDesignMode()) {
        this.pmClose();
        return;
      }

      this.pmStoreVerification();
      this.pmClose();
      this.dispatchEvent(new CustomEvent('pm:age-verified', {
        bubbles: true,
        detail: { pmMinimumAge: this.pmGetMinimumAge() }
      }));
    }

    pmHandleDecline() {
      if (this.pmIsDesignMode()) {
        this.pmClose();
        return;
      }

      const pmRootUrl = this.dataset.pmAgeVerificationRootUrl || '/';
      let pmRedirectUrl = this.dataset.pmAgeVerificationRedirectUrl || pmRootUrl;

      try {
        const pmCandidateUrl = new URL(pmRedirectUrl, window.location.origin);
        const pmRootCandidateUrl = new URL(pmRootUrl, window.location.origin);
        const pmCurrentUrl = new URL(window.location.href);
        const pmIsCurrentUrl = pmCandidateUrl.href === pmCurrentUrl.href;
        pmRedirectUrl = pmCandidateUrl.origin === window.location.origin && !pmIsCurrentUrl ? pmCandidateUrl.href : pmRootCandidateUrl.href;
      } catch (pmRedirectError) {
        pmRedirectUrl = pmRootUrl;
      }

      window.location.replace(pmRedirectUrl);
    }

    pmHandlePageShow(pmEvent) {
      if (this.pmIsDesignMode()) {
        return;
      }

      if (pmEvent.persisted) {
        this.pmOpenIfNeeded();
      }
    }

    pmHandleSectionSelect(pmEvent) {
      if (pmEvent.target !== this.pmGetSectionElement()) {
        return;
      }

      this.pmOpen();
    }

    pmHandleSectionDeselect(pmEvent) {
      if (pmEvent.target === this.pmGetSectionElement()) {
        this.pmClose();
      }
    }
  }

  if (!customElements.get('pm-age-verification')) {
    customElements.define('pm-age-verification', PmAgeVerification);
  }

  class PmProductGalleryControls extends HTMLElement {
    constructor() {
      super();
      this.pmAbortController = null;
      this.pmCarousel = null;
    }

    connectedCallback() {
      this.pmAbortController?.abort();
      this.pmAbortController = new AbortController();
      this.pmCarousel = document.getElementById(this.getAttribute('aria-controls'));

      if (!this.pmCarousel) {
        return;
      }

      const pmSignal = this.pmAbortController.signal;
      this.pmCarousel.addEventListener('carousel:change', this.pmUpdateMediaVisibility.bind(this), { signal: pmSignal });
      this.pmCarousel.addEventListener('carousel:filter', () => requestAnimationFrame(() => this.pmUpdateMediaVisibility()), { signal: pmSignal });
      requestAnimationFrame(() => this.pmUpdateMediaVisibility());
    }

    disconnectedCallback() {
      this.pmAbortController?.abort();
      this.pmAbortController = null;
      this.pmCarousel = null;
    }

    pmUpdateMediaVisibility() {
      const pmSelectedCell = this.pmCarousel?.selectedCell;

      if (!pmSelectedCell) {
        return;
      }

      this.hidden = pmSelectedCell.dataset.mediaType !== 'image';
    }
  }

  if (!customElements.get('pm-product-gallery-controls')) {
    customElements.define('pm-product-gallery-controls', PmProductGalleryControls);
  }

  const pmComplementaryMobileMediaQuery = window.matchMedia('(max-width: 999px)');
  const pmComplementarySourceAnchors = new WeakMap();
  let pmComplementarySyncFrame = null;

  function pmCreateComplementarySourceAnchor(pmSource) {
    let pmSourceAnchor = pmComplementarySourceAnchors.get(pmSource);

    if (!pmSourceAnchor) {
      pmSourceAnchor = document.createComment('pm-complementary-source-position');
      pmSource.parentNode?.insertBefore(pmSourceAnchor, pmSource);
      pmComplementarySourceAnchors.set(pmSource, pmSourceAnchor);
    }

    return pmSourceAnchor;
  }

  function pmCaptureComplementaryScrollPositions(pmSource) {
    return Array.from(pmSource.querySelectorAll('.scroll-area')).map((pmCarousel) => ({
      pmCarousel,
      pmScrollLeft: pmCarousel.scrollLeft,
      pmScrollTop: pmCarousel.scrollTop
    }));
  }

  function pmRestoreComplementaryScrollPositions(pmScrollPositions) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        pmScrollPositions.forEach(({ pmCarousel, pmScrollLeft, pmScrollTop }) => {
          pmCarousel.scrollTo({ left: pmScrollLeft, top: pmScrollTop, behavior: 'auto' });
        });
      });
    });
  }

  function pmMoveComplementarySource(pmSource, pmTarget) {
    const pmScrollPositions = pmCaptureComplementaryScrollPositions(pmSource);
    pmTarget.append(pmSource);
    pmRestoreComplementaryScrollPositions(pmScrollPositions);
  }

  function pmRestoreComplementarySource(pmSource, pmSourceAnchor) {
    const pmScrollPositions = pmCaptureComplementaryScrollPositions(pmSource);
    pmSourceAnchor.after(pmSource);
    pmRestoreComplementaryScrollPositions(pmScrollPositions);
  }

  function pmSyncComplementaryPosition() {
    pmComplementarySyncFrame = null;

    document.querySelectorAll('product-recommendations[data-pm-complementary-source]').forEach((pmSource) => {
      const pmProductRerender = pmSource.closest('product-rerender');
      const pmTarget = pmProductRerender?.querySelector('[data-pm-complementary-anchor]');

      if (!pmTarget) {
        return;
      }

      if (pmComplementaryMobileMediaQuery.matches) {
        const pmSourceAnchor = pmCreateComplementarySourceAnchor(pmSource);

        if (pmSource.parentElement !== pmTarget) {
          pmMoveComplementarySource(pmSource, pmTarget);
        }
      } else {
        const pmSourceAnchor = pmComplementarySourceAnchors.get(pmSource);

        if (pmSourceAnchor?.parentNode && pmSource.previousSibling !== pmSourceAnchor) {
          pmRestoreComplementarySource(pmSource, pmSourceAnchor);
        }
      }
    });
  }

  function pmScheduleComplementaryPositionSync() {
    if (pmComplementarySyncFrame === null) {
      pmComplementarySyncFrame = requestAnimationFrame(pmSyncComplementaryPosition);
    }
  }

  pmComplementaryMobileMediaQuery.addEventListener('change', pmScheduleComplementaryPositionSync);
  document.addEventListener('shopify:section:load', pmScheduleComplementaryPositionSync);

  const pmComplementaryMain = document.getElementById('main');
  if (pmComplementaryMain) {
    new MutationObserver(pmScheduleComplementaryPositionSync).observe(pmComplementaryMain, { childList: true, subtree: true });
  }

  pmSyncComplementaryPosition();

})();
